import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, AlertTriangle, RotateCcw, Sliders, Calculator, ShieldAlert, Bot } from 'lucide-react';
import { RISK_FACTORS } from '../utils/riskCalculation';
import { RiskTuningChat } from './RiskTuningChat';
import { useEscapeKey } from '../hooks/useEscapeKey';
interface RiskMethodologyModalProps {
  onClose: () => void;
  initialTab?: 'formula' | 'tune' | 'ai_advisor';
}
export function RiskMethodologyModal({
  onClose,
  initialTab = 'formula'
}: RiskMethodologyModalProps) {
  useEscapeKey(onClose);
  const [activeTab, setActiveTab] = useState<'formula' | 'tune' | 'ai_advisor'>(initialTab);
  // Tuning state
  const [weights, setWeights] = useState<Record<string, number>>({
    visual_hazards: 40,
    human_factors: 25,
    operational_friction: 20,
    documentation_quality: 15
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const totalWeight = Object.values(weights).reduce((a, b) => a + b, 0);
  const handleWeightChange = (key: string, value: number) => {
    setWeights(prev => ({
      ...prev,
      [key]: value
    }));
  };
  // Check for extreme weight values
  const getExtremeWeights = () => {
    const warnings: {
      factor: string;
      value: number;
      type: 'low' | 'high' | 'disabled';
    }[] = [];
    Object.entries(weights).forEach(([key, value]) => {
      const factor = RISK_FACTORS[key];
      if (!factor) return;
      if (value === 0) {
        warnings.push({
          factor: factor.name,
          value,
          type: 'disabled'
        });
      } else if (value < 5) {
        warnings.push({
          factor: factor.name,
          value,
          type: 'low'
        });
      }
      if (value > 60) {
        warnings.push({
          factor: factor.name,
          value,
          type: 'high'
        });
      }
    });
    return warnings;
  };
  const handleApply = () => {
    const extremeWeights = getExtremeWeights();
    if (extremeWeights.length > 0) {
      setShowConfirmation(true);
    } else {
      // Apply directly — no extreme values
      onClose();
    }
  };
  const handleConfirmApply = () => {
    setShowConfirmation(false);
    onClose();
  };
  return <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-3634" data-mp="1b8iup096bbxq">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-black/40 backdrop-blur-sm" data-id="element-3635" data-mp="1g2mzpdyvp9w6" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]" data-id="element-3636" data-mp="1u266o3fyef6w">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between flex-shrink-0" data-id="element-3637" data-mp="3t93ewpgvobd0">
          <div className="flex items-center gap-3" data-id="element-3638" data-mp="3oga87w1umsya">
            <div className="p-2 bg-indigo-100 rounded-lg" data-id="element-3639" data-mp="3ji8hcoy6lpc9">
              <Sparkles size={20} className="text-indigo-600" data-id="element-3640" data-mp="3jiryi3g0c9p8" />
            </div>
            <h2 className="text-xl font-bold text-gray-900" data-id="element-3641" data-mp="3ek6x40p0rjz7">
              Risk Methodology
            </h2>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-3642" data-mp="g0qlzgzliasq">
            <X size={20} data-id="element-3643" data-mp="62atb9kx6th4" />
          </button>
        </div>

        <div className="flex border-b border-gray-100 px-6 pt-2 flex-shrink-0 overflow-x-auto" data-id="element-3644" data-mp="21qxqafzvxx3">
          <button onClick={() => setActiveTab('formula')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${activeTab === 'formula' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-3645" data-mp="243famhuc69m">
            <Calculator size={16} data-id="element-3646" data-mp="ja9orhp08gpo" />
            How We Calculate
          </button>

          <button onClick={() => setActiveTab('tune')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${activeTab === 'tune' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-3647" data-mp="4ldklvq2jnuc">
            <Sliders size={16} data-id="element-3648" data-mp="127t390u8erjh" />
            Tune Weights
          </button>

          <button onClick={() => setActiveTab('ai_advisor')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${activeTab === 'ai_advisor' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-3649" data-mp="o9mafz453hqo">
            <Bot size={16} data-id="element-3650" data-mp="3d2gq8a99yx5w" />
            AI Advisor
            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wide rounded-full" data-id="element-3651" data-mp="35h4i98ljvx0i">
              Beta
            </span>
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1" data-id="element-3652" data-mp="3ffah1hc28n15">
          {activeTab === 'formula' && <div className="space-y-6" data-id="element-3653" data-mp="3agqvhi1dpd9b">
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 text-center" data-id="element-3654" data-mp="3ahshghjxnk5t">
                <p className="text-sm text-gray-500 uppercase tracking-wider font-semibold mb-4" data-id="element-3655" data-mp="2mrofeeme7sgx">
                  The Formula
                </p>
                <div className="text-lg md:text-xl font-mono font-bold text-gray-900 bg-white py-4 px-6 rounded-lg shadow-sm border border-gray-100 inline-block" data-id="element-3656" data-mp="2wgptfz9dnzs9">
                  Risk Score = Σ (Factor Score × Weight)
                </div>
                <p className="text-sm text-gray-600 mt-4 max-w-lg mx-auto" data-id="element-3657" data-mp="23zdgswyrugom">
                  Each factor is scored 0-100 based on real-time site data, then
                  weighted by its historical correlation with incidents.
                </p>
              </div>

              <div className="space-y-3" data-id="element-3658" data-mp="2sqt8bul4nb6i">
                <h3 className="font-bold text-gray-900" data-id="element-3659" data-mp="2spqdxcoliqlu">Default Weights</h3>
                {Object.values(RISK_FACTORS).map(factor => <div key={factor.id} className="flex items-center justify-between p-3 bg-white border border-gray-100 rounded-lg" data-id="element-3660" data-mp="32guvmk5nopkd">
                    <span className="font-medium text-gray-900" data-id="element-3661" data-mp="25sydhauklm71">
                      {factor.name}
                    </span>
                    <span className="font-bold text-indigo-600" data-id="element-3662" data-mp="2eqqtkh848xvh">
                      {factor.defaultWeight * 100}%
                    </span>
                  </div>)}
              </div>
            </div>}

          {activeTab === 'tune' && <div className="space-y-6" data-id="element-3663" data-mp="3q8t6j3170iwz">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3" data-id="element-3664" data-mp="3v4qmh88jnn98">
                <AlertTriangle className="text-amber-600 flex-shrink-0" size={20} data-id="element-3665" data-mp="1jig6ombx6xua" />
                <div data-id="element-3666" data-mp="1ejvftdik5qii">
                  <h4 className="font-bold text-amber-900 text-sm" data-id="element-3667" data-mp="1elfzu978xr24">
                    Customizing the Model
                  </h4>
                  <p className="text-amber-800 text-sm mt-1" data-id="element-3668" data-mp="14ttomcuwrrzd">
                    Adjusting weights will recalculate all risk scores for your
                    view. Weights must total exactly 100%.
                  </p>
                </div>
              </div>

              <div className="space-y-6" data-id="element-3669" data-mp="vuyjl43lonxn">
                {Object.entries(RISK_FACTORS).map(([key, factor]) => {
              const value = weights[key];
              const showLowWarning = value > 0 && value < 5;
              const showHighWarning = value > 60;
              return <div key={key} className="space-y-2" data-id="element-3670" data-mp="2g8fo76a1t6p0">
                      <div className="flex justify-between items-center" data-id="element-3671" data-mp="2g8ywdv9wcgpu">
                        <label className="font-medium text-gray-900" data-id="element-3672" data-mp="1p2tm2x99r0dt">
                          {factor.name}
                        </label>
                        <span className="font-bold text-gray-700 w-12 text-right" data-id="element-3673" data-mp="1u4aiafgt5yo2">
                          {value}%
                        </span>
                      </div>
                      <input type="range" min="0" max="100" value={value} onChange={e => handleWeightChange(key, parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600" data-id="element-3674" data-mp="1kb1zext8hesy" />

                      {showLowWarning && <p className="text-xs text-amber-600 flex items-center gap-1 mt-1" data-id="element-3675" data-mp="2rqg8zrgd8l4v">
                          <AlertTriangle size={12} data-id="element-3676" data-mp="2vq8mmikm9hh3" />
                          Values below 5% will have negligible effect on risk
                          scores.
                        </p>}
                      {showHighWarning && <p className="text-xs text-amber-600 flex items-center gap-1 mt-1" data-id="element-3677" data-mp="ttq08nkcvf51">
                          <AlertTriangle size={12} data-id="element-3678" data-mp="p3jbmnpufums" />
                          Values above 60% will cause this factor to dominate
                          all scores.
                        </p>}
                      {value === 0 && <p className="text-xs text-amber-600 flex items-center gap-1 mt-1" data-id="element-3679" data-mp="yst4i5sihhby">
                          <AlertTriangle size={12} data-id="element-3680" data-mp="1csc1c2fvh68y" />
                          This factor is completely disabled.
                        </p>}
                    </div>;
            })}
              </div>

              <div className="pt-4 border-t border-gray-100 flex items-center justify-between" data-id="element-3681" data-mp="39ldugb7zitk5">
                <div className="flex items-center gap-2" data-id="element-3682" data-mp="g5fauw0r87bi">
                  <span className="text-sm font-medium text-gray-600" data-id="element-3683" data-mp="g6yqckel6ec8">
                    Total:
                  </span>
                  <span className={`text-lg font-bold ${totalWeight === 100 ? 'text-emerald-600' : 'text-red-600'}`} data-id="element-3684" data-mp="b8x821ozij1t">
                    {totalWeight}%
                  </span>
                </div>
                <div className="flex gap-3" data-id="element-3685" data-mp="yweurite6qxx">
                  <button onClick={() => setWeights({
                visual_hazards: 40,
                human_factors: 25,
                operational_friction: 20,
                documentation_quality: 15
              })} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2" data-id="element-3686" data-mp="yy9eitsxgd0f">
                    <RotateCcw size={16} data-id="element-3687" data-mp="1l4k8oz4qxkiu" />
                    Reset
                  </button>
                  <button disabled={totalWeight !== 100} onClick={handleApply} className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${totalWeight === 100 ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-3688" data-mp="1l6wwhsx6qp3t">
                    Apply to My View
                  </button>
                </div>
              </div>
            </div>}

          {activeTab === 'ai_advisor' && <div className="h-[500px] flex flex-col" data-id="element-3689" data-mp="2dthsj6olvpdp">
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 flex gap-3 mb-4 flex-shrink-0" data-id="element-3690" data-mp="2du18ihup43p3">
                <Sparkles className="text-indigo-600 flex-shrink-0" size={20} data-id="element-3691" data-mp="2iuf9jfxvqddm" />
                <div data-id="element-3692" data-mp="2mvqzxnokv2mv">
                  <h4 className="font-bold text-indigo-900 text-sm" data-id="element-3693" data-mp="2ms36eoj4ug85">
                    Conversational Tuning
                  </h4>
                  <p className="text-indigo-800 text-sm mt-1" data-id="element-3694" data-mp="rq9eg5rfc9di">
                    Tell Bilton AI about your site conditions or safety
                    priorities, and it will automatically adjust the risk
                    weights for you.
                  </p>
                </div>
              </div>

              <div className="flex-1 min-h-0" data-id="element-3695" data-mp="wpm6d4x5k6my">
                <RiskTuningChat weights={weights} onWeightsChange={setWeights} data-id="element-3696" data-mp="wnrk9na5a8ha" />
              </div>

              <div className="pt-4 mt-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0" data-id="element-3697" data-mp="16em4yn7qt00m">
                <div className="flex items-center gap-2" data-id="element-3698" data-mp="1ke5974xfnr7n">
                  <span className="text-sm font-medium text-gray-600" data-id="element-3699" data-mp="1fglp3oe20k7s">
                    Current Total:
                  </span>
                  <span className={`text-lg font-bold ${totalWeight === 100 ? 'text-emerald-600' : 'text-red-600'}`} data-id="element-3700" data-mp="3m7l3cbsqbo7d">
                    {totalWeight}%
                  </span>
                </div>
                <div className="flex gap-3" data-id="element-3701" data-mp="1ws0mkg9oyg6r">
                  <button onClick={() => setWeights({
                visual_hazards: 40,
                human_factors: 25,
                operational_friction: 20,
                documentation_quality: 15
              })} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2" data-id="element-3702" data-mp="21p0u6vm6vqzz">
                    <RotateCcw size={16} data-id="element-3703" data-mp="14ztfo7jzmi5k" />
                    Reset
                  </button>
                  <button disabled={totalWeight !== 100} onClick={handleApply} className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${totalWeight === 100 ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-3704" data-mp="190v7427nxpwz">
                    Apply to My View
                  </button>
                </div>
              </div>
            </div>}
        </div>

        {/* Confirmation Modal for Extreme Weights */}
        <AnimatePresence data-id="element-3705" data-mp="24r40q11pjf62">
          {showConfirmation && <div className="absolute inset-0 z-10 flex items-center justify-center p-6" data-id="element-3706" data-mp="1zpem6bfa0jd8">
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="absolute inset-0 bg-black/30 rounded-xl" onClick={() => setShowConfirmation(false)} data-id="element-3707" data-mp="1vpnwyc5eebat" />
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} className="relative bg-white rounded-xl shadow-2xl border border-amber-200 w-full max-w-md p-6" data-id="element-3708" data-mp="1bzk8xsr1ossg">
                <div className="flex items-center gap-3 mb-4" data-id="element-3709" data-mp="1gigmk54c18w">
                  <div className="p-2 bg-amber-100 rounded-lg" data-id="element-3710" data-mp="1fz8tw96mt8l">
                    <ShieldAlert size={20} className="text-amber-600" data-id="element-3711" data-mp="2zzfmonpzqgir" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-3712" data-mp="34yiu2etg0ytw">
                    Confirm Weight Changes
                  </h3>
                </div>

                <p className="text-sm text-gray-600 mb-4" data-id="element-3713" data-mp="39ur2zah1rvsm">
                  Your configuration includes factor weights outside the
                  recommended range. This may affect the accuracy of risk
                  predictions.
                </p>

                <div className="space-y-2 mb-6" data-id="element-3714" data-mp="2m5omw722ab08">
                  {getExtremeWeights().map((warning, i) => <div key={i} className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-lg" data-id="element-3715" data-mp="2e4h1emg84epk" data-mpi={i}>
                      <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" data-id="element-3716" data-mp="2nus82gral205" />
                      <div className="text-sm" data-id="element-3717" data-mp="1v6wcjfdglg4y">
                        <span className="font-semibold text-amber-900" data-id="element-3718" data-mp="1v7pkuks2gdp7">
                          {warning.factor}
                        </span>
                        <span className="text-amber-800" data-id="element-3719" data-mp="25652lx57eam1">
                          {warning.type === 'disabled' && <>
                              {' '}
                              is completely disabled (0%). This factor will not
                              contribute to any risk score.
                            </>}
                          {warning.type === 'low' && <>
                              {' '}
                              is set to {warning.value}%, which will have
                              negligible effect on risk scores.
                            </>}
                          {warning.type === 'high' && <>
                              {' '}
                              is set to {warning.value}%, which means this
                              single factor will dominate all risk scores.
                            </>}
                        </span>
                      </div>
                    </div>)}
                </div>

                <div className="flex gap-3 justify-end" data-id="element-3720" data-mp="1c92yb4j5c57o">
                  <button onClick={() => setShowConfirmation(false)} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-3721" data-mp="1zzqese74o35u">
                    Go Back & Adjust
                  </button>
                  <button onClick={handleConfirmApply} className="px-4 py-2.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors" data-id="element-3722" data-mp="3w34pyzoovud4">
                    Apply Anyway
                  </button>
                </div>
              </motion.div>
            </div>}
        </AnimatePresence>
      </motion.div>
    </div>;
}